/**
 * Config sealed into the bundle at build time.
 *
 * A sealed build carries the normalized `SdkConfig` for exactly one website, so
 * `init()` never calls the pool. The build is responsible for what it seals:
 *
 * - `geo` must be left empty. It describes the visitor, not the site; sealing it
 *   would stamp the build machine's country onto every event. The tracker
 *   resolves the country from its own edge headers.
 * - `cmp.required` / `cmp.ccpa_applicable` come from the variant being built, not
 *   from the pool response, for the same reason.
 * - Everything sealed is public. The bundle is a JavaScript file on a CDN.
 *
 * `null` in both constants means a runtime-config build.
 */

import type { SdkConfig } from './types';
import type { InitOptions } from '../options';

/** Deep copy so `init()` can apply overrides without mutating the baked constant. */
function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function readSealedConfig(): SdkConfig | null {
  const raw = __SEALED_CONFIG__;
  return raw ? clone(raw as SdkConfig) : null;
}

/**
 * Default init options baked in for this site — the lowest-precedence layer in
 * `resolveOptions`, below `data-kz-*`. Carries `mbp: true` for media-buying
 * sites, which otherwise wait for the host to opt in.
 */
export function readSealedOptions(): InitOptions | null {
  const raw = __SEALED_OPTIONS__;
  return raw ? clone(raw as InitOptions) : null;
}

export function isSealedBuild(): boolean {
  return Boolean(__SEALED_CONFIG__);
}
